import { jsx as _jsx } from "preact/jsx-runtime";
import { useEffect, useRef } from 'preact/hooks';
/**
 * `onKeyDown` for a dialog or menu the browser closes on Escape: keeps the
 * Dashboard from treating the same key press as "close the modal".
 */
export function stopEscapePropagation(event) {
    if (event.key === 'Escape')
        event.stopPropagation();
}
const isOnBackdrop = (event) => {
    const dialog = event.currentTarget;
    if (event.target !== dialog)
        return false;
    const rect = dialog.getBoundingClientRect();
    return (event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom);
};
/**
 * A native modal `<dialog>`, shown with `showModal()` once mounted: focus
 * trap, `::backdrop` and focus restore come from the browser (engines without
 * `showModal` get the same dialog inline, without the trap). Escape, a
 * click on the backdrop and the browser's other close requests (the `cancel`
 * event) call `onDismiss`; the dialog stays open until its
 * owner unmounts it, and is closed first, as removing an open modal from the
 * DOM would skip the focus restore.
 */
export default function ModalDialog({ onDismiss, children, ...attributes }) {
    const ref = useRef(null);
    // Only a click that also started on the backdrop dismisses: selecting text
    // in the dialog and releasing outside it is not one.
    const pressedBackdrop = useRef(false);
    useEffect(() => {
        const dialog = ref.current;
        if (!dialog)
            return;
        const opener = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        if (typeof dialog.showModal === 'function')
            dialog.showModal();
        else {
            // No focus trap without showModal(): focus the dialog's first button
            // ourselves so Escape reaches it and not the page behind it.
            dialog.setAttribute('open', '');
            dialog.querySelector('button')?.focus();
        }
        return () => {
            if (dialog.open && typeof dialog.close === 'function')
                dialog.close();
            // The browser returns focus to the opener, but not to one that is gone,
            // and not at all for a dialog opened without showModal().
            const { activeElement } = document;
            if (activeElement !== document.body && !dialog.contains(activeElement))
                return;
            if (opener?.isConnected)
                opener.focus();
        };
    }, []);
    return (_jsx("dialog", { ...attributes, ref: ref, onCancel: (event) => {
            event.preventDefault();
            onDismiss();
        }, onPointerDown: (event) => {
            pressedBackdrop.current = isOnBackdrop(event);
        }, onClick: (event) => {
            if (pressedBackdrop.current && isOnBackdrop(event))
                onDismiss();
            pressedBackdrop.current = false;
        }, onKeyDown: (event) => {
            // Escape while composing text (IME) cancels the composition only.
            if (event.key !== 'Escape' || event.isComposing)
                return;
            // Handled here rather than through the `cancel` event, which only a
            // modal dialog fires; and the Dashboard must not treat the same key
            // press as "close the modal".
            event.preventDefault();
            event.stopPropagation();
            onDismiss();
        }, children: children }));
}
